import {
	ReactElement,
	RefObject,
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState
} from "react";
import { AddTileCard } from "../AddTileCard/AddTileCard";
import {
	Button,
	ContentWithSidebar,
	Dialog,
	SearchInput,
	SidebarHandle,
	Toolbar
} from "client/Components";
import { localize } from "client_legacy/ndaw/culture";
import {
	allAppsCategory,
	categories,
	CategoryEntry,
	CategoryFilter
} from "../CategoryFilter";
import { newGuid } from "client_legacy/ndaw/guid";
import {
	AlertManager,
	MediaQueryKind,
	useAlerts,
	useUserScopedKey,
	useWorkspaceMediaQuery
} from "client/Hooks";
import { AppStoreSidebarKey } from "../AppStore";
import {
	AddTileTargetModel,
	addTilesToWorkspace,
	AvailableAppModel,
	AvailableAppsViewModel,
	fetchTiles
} from "client/Api/Workspace/workspace";
import { SpaceListItem } from "client/Api/Apps/AppStore/appStore";
import { AppCategory } from "client/Api/Apps/common";
import { AddTilesButton } from "./AddTilesButton/AddTilesButton";
import { filterTilesBySearchTermAndCategory } from "../CreateNewTile";
import { navigateToWorkspace } from "client/Features/Workspace/routing";
import { isCancelledError } from "client_legacy/ndaw/cancellation";
import { formatList } from "client/Misc/cultureSpecificUtils";
import { AppCardList } from "../AppCardList";
import { AppStoreLayout } from "../AppStoreLayout";

export type AddTilesModel = Exclude<AddTileTargetModel, "spaceId"> & {
	spaceId: number | null;
};

type AddTilesProps = {
	targetTileGroupId: number | null;
	targetSpace: SpaceListItem | null;
	activeSpaceId: number | null;
	onClose: () => void;
	onCreateTileClick: () => void;
	setIsBusy: (value: boolean) => void;
	onRefreshWorkspace: () => Promise<void>;
	onSwitchWorkspaceToEditMode: () => void;
};

export const AddTiles = (props: AddTilesProps): ReactElement => {
	const {
		targetTileGroupId,
		targetSpace,
		activeSpaceId,
		onClose,
		onCreateTileClick,
		setIsBusy,
		onRefreshWorkspace,
		onSwitchWorkspaceToEditMode
	} = props;

	const ref: RefObject<HTMLDivElement | null> = useRef<HTMLDivElement>(null);
	const persistedKey: string = useUserScopedKey(AppStoreSidebarKey);
	const [currentCategory, setCurrentCategory] =
		useState<CategoryEntry>(allAppsCategory);
	const [selectedTiles, setSelectedTiles] = useState<Set<number>>(new Set());
	const [allTiles, setAllTiles] = useState<readonly AvailableAppModel[]>([]);
	const [searchValue, setSearchValue] = useState<VersionedValue<string>>({
		value: "",
		version: newGuid(),
	});

	const filteredTiles = useMemo(
		(): readonly AvailableAppModel[]=> {
			return filterTilesBySearchTermAndCategory(
				allTiles,
				searchValue.value,
				currentCategory.id
			);
		},
		[
			allTiles,
			currentCategory.id,
			searchValue.value
		]
	);

	const tilesCategories = useMemo(
		(): Set<AppCategory | null> => {
			return new Set<AppCategory | null>(
				allTiles.map(x => x.appCategory)
			);
		},
		[allTiles]
	);

	const alertManager: AlertManager = useAlerts();
	const isMobile: boolean = useWorkspaceMediaQuery(MediaQueryKind.Mobile);
	const sidebarRef: RefObject<SidebarHandle | null> = useRef<SidebarHandle>(null);

	const toggleCard = useCallback(
		(appId: number): void => {
			setSelectedTiles(prevState => {
				const newTilesSet = new Set(prevState);

				if (newTilesSet.has(appId)) {
					newTilesSet.delete(appId);
				} else {
					newTilesSet.add(appId);
				}

				return newTilesSet;
			});
		},
		[]
	);

	const createMessage = (
		addedApps: Pick<AvailableAppModel, "name" | "installedAppId">[]
	): string => {
		const appNames: string[] = addedApps.map((app) => `'${app.name}'`);

		if (appNames.length > 1) {
			return localize("_NewAppsAdded")
				.replace(
					"{0}",
					formatList(appNames)
				);
		}

		return localize("_NewAppAdded").replace("{0}", appNames[0]);
	};

	const submitSelectedTiles = useCallback(
		async (target: AddTilesModel): Promise<void> => {
			const addedApps: Pick<
				AvailableAppModel,
				"name" | "installedAppId"
			>[] = allTiles.reduce<
				Pick<AvailableAppModel, "name" | "installedAppId">[]>(
				(acc, tile) => {
					if (selectedTiles.has(tile.installedAppId)) {
						acc.push({
							name: tile.name,
							installedAppId: tile.installedAppId,
						});
					}

					return acc;
				},
				[]
			);

			try {
				const shouldRedirect: boolean = target.spaceId !== activeSpaceId;

				setIsBusy(true);

				await addTilesToWorkspace(target, addedApps);

				setIsBusy(false);

				onClose();

				await onRefreshWorkspace();
				alertManager.showSuccess(createMessage(addedApps));

				if (shouldRedirect) {
					navigateToWorkspace(target.spaceId ?? undefined);

					return;
				}

				onSwitchWorkspaceToEditMode();
			} catch (e) {
				console.error(e);

				setIsBusy(false);

				alertManager.showError(localize("_GeneralErrorMessage"));
			}
		},
		[
			allTiles,
			selectedTiles,
			activeSpaceId,
			setIsBusy,
			onClose,
			onRefreshWorkspace,
			alertManager,
			onSwitchWorkspaceToEditMode
		]
	);

	useEffect(
		(): (() => void) => {
			const getTiles = async (signal: AbortSignal) => {
				try {
					const result: AvailableAppsViewModel = await fetchTiles(signal);

					setAllTiles(result.availableApps);
				} catch (e) {
					if (isCancelledError(e)) {
						return;
					}

					alertManager.showError(localize("_GeneralErrorMessage"));
					console.error(e);
				}
			};

			const cancellation = new AbortController();
			getTiles(cancellation.signal);
			return (): void => cancellation.abort();
		},
		[alertManager, setAllTiles]
	);

	const tileCards: readonly ReactElement[] = filteredTiles.filter(x => !x.tileCanBeConfigured)
		.sort((a, b) => a.name.localeCompare(b.name))
		.map((tile) => (
			<AddTileCard
				key={tile.installedAppId}
				tile={tile}
				onClick={toggleCard}
				isSelected={selectedTiles.has(tile.installedAppId)}
			/>
		));

	const availableCategories: CategoryEntry[] = useMemo(
		() => {
			return categories.filter((category) => tilesCategories.has(category.id) || !category.id);
		},
		[tilesCategories]
	);

	const changeCategoryFilterHandler = useCallback(
		(category: CategoryEntry): void => {
			if (isMobile) {
				sidebarRef.current
					?.onClose();
			}

			setCurrentCategory(category);
		},
		[isMobile]
	);

	return (
		<>
			<Dialog.Body goesEdgeToEdge>
				<ContentWithSidebar trackingId="add-tiles">
					<ContentWithSidebar.Sidebar
						persistentKeyName={persistedKey}
						ref={sidebarRef}
					>
						<CategoryFilter
							items={availableCategories}
							value={currentCategory}
							onChange={changeCategoryFilterHandler}
						/>
					</ContentWithSidebar.Sidebar>
					<ContentWithSidebar.Content ref={ref} hasBackgroundColor>
						<AppStoreLayout>
							<AppStoreLayout.Header
								actions={
									<Button
										kind="primary"
										text={localize("_CreateTile")}
										onClick={onCreateTileClick}
										trackingId="create-new-tile"
									/>
								}
								search={
									<SearchInput
										onChange={(value, version) =>
											setSearchValue({ value, version })
										}
										value={searchValue}
										placeholder={localize("_Search")}
										trackingId="add-tiles-search"
										variant="white"
									/>
								}
							/>

							<AppStoreLayout.CategoryName name={currentCategory.name} />

							{tileCards.length > 0
								? (
									<AppCardList trackingId="card-list">
										{tileCards}
									</AppCardList>
								)
								: <p>{localize("_NoResultsFound")}</p>
							}
						</AppStoreLayout>
					</ContentWithSidebar.Content>
				</ContentWithSidebar>
			</Dialog.Body>
			<Dialog.Footer>
				<Toolbar>
					<AddTilesButton
						targetSpace={targetSpace}
						targetTileGroupId={targetTileGroupId}
						isDisabled={selectedTiles.size < 1}
						onClick={submitSelectedTiles}
					/>
					<Toolbar.Button
						kind="secondary"
						onClick={onClose}
						text={localize("_Close")}
						trackingId="close-modal"
						responsiveBehavior="hide"
					/>
				</Toolbar>
			</Dialog.Footer>
		</>
	);
};

AddTiles.displayName = "AddTiles";
